import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS, REWARD_TYPE_STYLES, REWARD_TYPES } from "../../constants";

const EditRewardDialog = ({ open, reward, onClose, onSaved }) => {
    const [form, setForm] = useState({ name: "", description: "", cost_points: 0, type: "POINTS", validity_days: "", active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (reward) {
            setForm({
                name: reward.name || "", description: reward.description || "",
                cost_points: reward.cost_points ?? 0, type: reward.type || "POINTS",
                validity_days: reward.validity_days ?? "", active: reward.active ?? true
            });
            setError(null);
        }
    }, [reward]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
        setForm((p) => ({ ...p, [field]: value }));
    };
    const isValid = form.name.trim() && form.type;

    const handleSubmit = async () => {
        if (!isValid || !reward) return;
        setSubmitting(true);
        setError(null);
        try {
            const body = {
                name: form.name.trim(), description: form.description.trim() || null,
                cost_points: form.cost_points || 0, type: form.type,
                validity_days: form.validity_days !== "" ? Number(form.validity_days) : null,
                active: form.active
            };
            const res = await fetch(`${LOYALTY_API_BASE}/rewards/${reward.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": reward.brand
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            onSaved(); onClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    if (!reward) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rewards.gradient }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>Modifier la Récompense</Typography>
                        <Typography fontSize={11} color="text.secondary">{reward.brand} • {reward.name}</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#f8f9ff", border: "1px solid #e0e7ff" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.rewards.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip label={reward.brand} size="small"
                        sx={{ fontWeight: 700, fontSize: 12, bgcolor: `${SECTION_COLORS.rewards.main}15`, color: SECTION_COLORS.rewards.main }} />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom" value={form.name} onChange={handleChange("name")} size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description} onChange={handleChange("description")}
                            size="small" multiline rows={2} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required select label="Type" value={form.type} onChange={handleChange("type")} size="small"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                            {REWARD_TYPES.map((t) => <MenuItem key={t} value={t}>{REWARD_TYPE_STYLES[t]?.label || t}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Coût (points)" type="number" value={form.cost_points}
                            onChange={handleChange("cost_points")} size="small" InputProps={{ inputProps: { min: 0 } }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Validité (jours)" type="number" value={form.validity_days}
                            onChange={handleChange("validity_days")} size="small" helperText="Laisser vide = illimité"
                            InputProps={{ inputProps: { min: 1 } }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={13} fontWeight={600}>Activer</Typography>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rewards.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rewards.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{ background: SECTION_COLORS.rewards.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditRewardDialog;
